import { useState } from 'react';
import { Lock, Eye, EyeOff, ChevronDown, ChevronUp } from 'lucide-react';
import apiFetch from '../apiClient';

const inputStyle = {
    backgroundColor: 'var(--color-surface)',
    border: '1px solid var(--color-border)',
    color: 'var(--color-text-primary)',
    padding: '0.75rem 1rem',
    borderRadius: '0.5rem'
};

const focusHandlers = {
    onFocus: (e) => e.target.style.borderColor = 'var(--color-primary)',
    onBlur: (e) => e.target.style.borderColor = 'var(--color-border)'
};

const emptyForm = { current_password: '', new_password: '', confirm_password: '' };

export function ChangePasswordCard() {
    const [expanded, setExpanded] = useState(false);
    const [form, setForm] = useState(emptyForm);
    const [showCurrent, setShowCurrent] = useState(false);
    const [showNew, setShowNew] = useState(false);
    const [showConfirm, setShowConfirm] = useState(false);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState(false);

    const reset = () => {
        setForm(emptyForm);
        setError(null);
    };

    const submit = async () => {
        setError(null);
        if (!form.current_password || !form.new_password || !form.confirm_password) {
            setError('Preencha todos os campos.');
            return;
        }
        if (form.new_password !== form.confirm_password) {
            setError('A nova senha e a confirmação não coincidem.');
            return;
        }
        setSaving(true);
        try {
            await apiFetch('/api/profile/change-password', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(form)
            });
            reset();
            setSuccess(true);
            setTimeout(() => {
                setSuccess(false);
                setExpanded(false);
            }, 1800);
        } catch (err) {
            setError(err.message);
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="card custom-card h-100">
            <div className="card-body p-4">
                <div
                    className="d-flex align-items-center justify-content-between"
                    style={{ cursor: 'pointer' }}
                    onClick={() => setExpanded(!expanded)}
                >
                    <div className="d-flex align-items-center gap-3">
                        <div className="icon-container">
                            <Lock size={16} className="text-muted-custom" />
                        </div>
                        <h3 className="h5 fw-semibold mb-0">Alterar Senha</h3>
                    </div>
                    {expanded ? <ChevronUp size={18} className="text-muted-custom" /> : <ChevronDown size={18} className="text-muted-custom" />}
                </div>

                {expanded && (
                    <div className="mt-4 pt-3" style={{ borderTop: '1px solid var(--color-border)' }}>
                        <div className="row g-3">
                            <div className="col-12">
                                <label className="form-label text-muted-custom small">Senha atual</label>
                                <div className="input-group">
                                    <input
                                        type={showCurrent ? 'text' : 'password'}
                                        className="form-control"
                                        placeholder="••••••••"
                                        value={form.current_password}
                                        onChange={(e) => setForm({ ...form, current_password: e.target.value })}
                                        style={{ ...inputStyle, borderRight: 'none', borderRadius: '0.5rem 0 0 0.5rem' }}
                                        {...focusHandlers}
                                    />
                                    <button
                                        type="button"
                                        className="input-group-text"
                                        style={{ backgroundColor: 'var(--color-surface)', border: '1px solid var(--color-border)', borderLeft: 'none', color: 'var(--color-text-secondary)', cursor: 'pointer' }}
                                        onClick={() => setShowCurrent(!showCurrent)}
                                        aria-label={showCurrent ? 'Ocultar senha' : 'Mostrar senha'}
                                    >
                                        {showCurrent ? <EyeOff size={16} /> : <Eye size={16} />}
                                    </button>
                                </div>
                            </div>

                            <div className="col-12 col-md-6">
                                <label className="form-label text-muted-custom small">Nova senha</label>
                                <div className="input-group">
                                    <input
                                        type={showNew ? 'text' : 'password'}
                                        className="form-control"
                                        placeholder="••••••••"
                                        value={form.new_password}
                                        onChange={(e) => setForm({ ...form, new_password: e.target.value })}
                                        style={{ ...inputStyle, borderRight: 'none', borderRadius: '0.5rem 0 0 0.5rem' }}
                                        {...focusHandlers}
                                    />
                                    <button
                                        type="button"
                                        className="input-group-text"
                                        style={{ backgroundColor: 'var(--color-surface)', border: '1px solid var(--color-border)', borderLeft: 'none', color: 'var(--color-text-secondary)', cursor: 'pointer' }}
                                        onClick={() => setShowNew(!showNew)}
                                        aria-label={showNew ? 'Ocultar senha' : 'Mostrar senha'}
                                    >
                                        {showNew ? <EyeOff size={16} /> : <Eye size={16} />}
                                    </button>
                                </div>
                            </div>

                            <div className="col-12 col-md-6">
                                <label className="form-label text-muted-custom small">Confirmar nova senha</label>
                                <div className="input-group">
                                    <input
                                        type={showConfirm ? 'text' : 'password'}
                                        className="form-control"
                                        placeholder="••••••••"
                                        value={form.confirm_password}
                                        onChange={(e) => setForm({ ...form, confirm_password: e.target.value })}
                                        style={{ ...inputStyle, borderRight: 'none', borderRadius: '0.5rem 0 0 0.5rem' }}
                                        {...focusHandlers}
                                    />
                                    <button
                                        type="button"
                                        className="input-group-text"
                                        style={{ backgroundColor: 'var(--color-surface)', border: '1px solid var(--color-border)', borderLeft: 'none', color: 'var(--color-text-secondary)', cursor: 'pointer' }}
                                        onClick={() => setShowConfirm(!showConfirm)}
                                        aria-label={showConfirm ? 'Ocultar senha' : 'Mostrar senha'}
                                    >
                                        {showConfirm ? <EyeOff size={16} /> : <Eye size={16} />}
                                    </button>
                                </div>
                            </div>
                        </div>

                        <div className="d-flex gap-2 justify-content-end mt-3">
                            <button className="btn btn-outline-custom" onClick={() => { reset(); setExpanded(false); }} disabled={saving}>Cancelar</button>
                            <button className="btn btn-primary-custom" onClick={submit} disabled={saving}>Salvar nova senha</button>
                        </div>
                        {error && <small className="text-danger-custom d-block mt-2 text-end">{error}</small>}
                        {success && <small className="text-primary-custom d-block mt-2 text-end">Senha alterada com sucesso</small>}
                    </div>
                )}
            </div>
        </div>
    );
}
